/**
 * Guide « Comment choisir son fournisseur de matcha » (templates/article.guide-fournisseur.json).
 *
 * - <mm-guide-test> : le test en 8 cartes. Sans JavaScript, les 8 critères restent une liste lisible
 *   (et indexée) ; le script les empile en paquet, une carte à la fois, et tient le score.
 * - <mm-guide-calc> : le calculateur de coût du matcha par tasse.
 * - [data-mg-copier] : copie le message à envoyer aux fournisseurs.
 * Les fonctions exportées sont pures et testées dans tests/guide-fournisseur.test.mjs.
 */

/** Place de la carte i quand l'étape `etape` est affichée. */
export function position(i, etape) {
  const d = i - etape;
  if (d < 0) return 'passee';
  if (d === 0) return 'actif';
  if (d === 1) return 'd1';
  if (d === 2) return 'd2';
  return 'loin';
}

/** Réponses (true, false ou null par critère) → nombre de oui, de réponses et critères à vérifier. */
export function bilan(reponses) {
  const manques = [];
  let oui = 0;
  let repondues = 0;
  reponses.forEach((r, i) => {
    if (r === true) oui++;
    if (r !== null && r !== undefined) repondues++;
    if (r !== true) manques.push(i);
  });
  return { oui, repondues, manques };
}

/** Remplace [cle] par sa valeur dans un gabarit traduit. */
export function remplir(modele, valeurs) {
  return String(modele ?? '').replace(/\[(\w+)\]/g, (tout, cle) => (cle in valeurs ? String(valeurs[cle]) : tout));
}

/**
 * Message du résultat pour `n` critères remplis. Avec `repondues` (panneau de score), un message
 * d'attente tant que les 8 cartes n'ont pas reçu de réponse.
 */
export function message(n, textes, repondues = 8) {
  const total = 8;
  if (repondues === 0) return { t: textes.vide_t, p: textes.vide_p };
  if (repondues < total) {
    const v = { n, r: repondues, reste: total - repondues };
    return { t: remplir(textes.cours_t, v), p: remplir(textes.cours_p, v) };
  }
  const v = { n, manque: total - n };
  if (n < 5) return { t: remplir(textes.bas_t, v), p: remplir(textes.bas_p, v) };
  if (n < total) return { t: remplir(textes.moyen_t, v), p: remplir(v.manque === 1 ? textes.moyen_p1 : textes.moyen_p, v) };
  return { t: remplir(textes.haut_t, v), p: remplir(textes.haut_p, v) };
}

/** Coût du matcha par tasse : prix au kilo HT `px`, grammes par boisson `g`, prix de vente HT `pv`. */
export function cout({ px, g, pv }) {
  const tasse = (px * g) / 1000;
  return { tasse, tasses: Math.floor(1000 / g), part: Math.round((tasse / pv) * 1000) / 10 };
}

const BORNES = { px: [20, 600], g: [1, 5], pv: [2, 12] };

/** Applique un pas (+ ou −) à une valeur du calculateur, dans ses bornes. */
export function pas(etat, cle, delta) {
  const [min, max] = BORNES[cle];
  const v = Math.round((etat[cle] + delta) * 100) / 100;
  return { ...etat, [cle]: Math.min(max, Math.max(min, v)) };
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  const lang = () => document.documentElement.lang || 'fr';
  const nombre = (x, d) => new Intl.NumberFormat(lang(), { minimumFractionDigits: d, maximumFractionDigits: d }).format(x);
  const euros = (x, d) => new Intl.NumberFormat(lang(), { style: 'currency', currency: 'EUR', minimumFractionDigits: d, maximumFractionDigits: d }).format(x);

  class MmGuideTest extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pret) return;
      this.dataset.pret = '1';
      this.textes = JSON.parse(this.querySelector('[data-textes]')?.textContent || '{}');
      this.cartes = [...this.querySelectorAll('[data-carte]')];
      this.resultat = this.querySelector('[data-resultat]');
      this.points = [...this.querySelectorAll('[data-point]')];
      this.reponses = this.cartes.map(() => null);
      this.etape = 0;
      this.classList.add('est-paquet');
      this.addEventListener('click', (e) => {
        const rep = e.target.closest('[data-rep]');
        if (rep) {
          const i = Number(rep.closest('[data-carte]').dataset.carte);
          this.reponses[i] = rep.dataset.rep === '1';
          this.aller(Math.min(8, i + 1));
          return;
        }
        const point = e.target.closest('[data-point]');
        if (point) return this.aller(Number(point.dataset.point));
        if (e.target.closest('[data-refaire]')) {
          this.reponses = this.cartes.map(() => null);
          this.aller(0);
        }
      });
      document.addEventListener('click', (e) => {
        const lien = e.target.closest('a[data-mg-aller]');
        if (lien) this.aller(Number(lien.dataset.mgAller), false);
      });
      this.aller(0, false);
    }

    aller(etape, focus = true) {
      this.etape = etape;
      const { oui, repondues, manques } = bilan(this.reponses);
      this.cartes.forEach((c, i) => {
        const r = this.reponses[i];
        c.className = `mg-carte ${position(i, etape)}${r === true ? ' oui' : r === false ? ' non' : ''}`;
        const visible = i === etape;
        c.toggleAttribute('inert', !visible);
        c.setAttribute('aria-hidden', String(!visible));
      });
      if (this.resultat) {
        const d = 8 - etape;
        this.resultat.className = `mg-carte mg-carte--resultat ${d === 0 ? 'actif' : d === 1 ? 'd1' : d === 2 ? 'd2' : 'loin'}`;
        this.resultat.toggleAttribute('inert', etape !== 8);
        this.resultat.setAttribute('aria-hidden', String(etape !== 8));
        const m = message(oui, this.textes);
        this.resultat.querySelector('[data-res-t]').textContent = m.t;
        this.resultat.querySelector('[data-res-p]').textContent = m.p;
        const liste = this.resultat.querySelector('[data-manques]');
        liste.replaceChildren(...manques.map((i) => Object.assign(document.createElement('li'), { textContent: this.cartes[i].querySelector('h3').textContent })));
        this.resultat.querySelector('[data-manques-bloc]').hidden = manques.length === 0;
      }
      this.points.forEach((p) => {
        const k = Number(p.dataset.point);
        const r = this.reponses[k];
        p.classList.toggle('ok', r === true);
        p.classList.toggle('ko', r === false);
        p.classList.toggle('cur', k === etape);
        if (k === etape) p.setAttribute('aria-current', 'step');
        else p.removeAttribute('aria-current');
      });
      const s = message(oui, this.textes, repondues);
      const degres = Math.round((oui / 8) * 360);
      document.querySelectorAll('[data-mg-n]').forEach((el) => (el.textContent = `${oui}/8`));
      document.querySelectorAll('[data-mg-anneau]').forEach((el) => el.style.setProperty('--mg-deg', `${degres}deg`));
      const t = this.querySelector('[data-score-t]');
      if (t) t.textContent = s.t;
      const p = this.querySelector('[data-score-p]');
      if (p) p.textContent = s.p;
      if (focus) {
        const cible = etape === 8 ? this.resultat : this.cartes[etape];
        cible?.querySelector('h3')?.focus({ preventScroll: true });
        if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  class MmGuideCalc extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pret) return;
      this.dataset.pret = '1';
      this.etat = { px: Number(this.dataset.px) || 150, g: Number(this.dataset.g) || 2.5, pv: Number(this.dataset.pv) || 5 };
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-pas]');
        if (!b) return;
        const [cle, delta] = b.dataset.pas.split(':');
        this.etat = pas(this.etat, cle, Number(delta));
        this.maj();
      });
      this.maj();
    }

    maj() {
      const { px, g, pv } = this.etat;
      const c = cout(this.etat);
      const sorties = { px: euros(px, 0), g: `${nombre(g, 1)} g`, pv: euros(pv, 2), tasse: euros(c.tasse, 2), tasses: nombre(c.tasses, 0), part: `${nombre(c.part, 1)} %` };
      for (const [cle, texte] of Object.entries(sorties)) {
        const el = this.querySelector(`[data-sortie="${cle}"]`);
        if (el) el.textContent = texte;
      }
    }
  }

  if (!customElements.get('mm-guide-test')) customElements.define('mm-guide-test', MmGuideTest);
  if (!customElements.get('mm-guide-calc')) customElements.define('mm-guide-calc', MmGuideCalc);

  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-mg-copier]');
    if (!b) return;
    const texte = document.getElementById(b.dataset.mgCopier)?.textContent ?? '';
    try {
      await navigator.clipboard.writeText(texte);
      b.textContent = b.dataset.copie;
    } catch {
      /* presse-papiers refusé : le message reste sélectionnable à la main */
    }
  });

  // Barre mobile du score : visible une fois le haut de page passé, cachée sur le formulaire du bas.
  const barre = document.querySelector('[data-mg-barre]');
  const hero = document.querySelector('.mg-hero');
  if (barre && hero && 'IntersectionObserver' in window) {
    const vus = { hero: true, bas: false };
    const maj = () => barre.classList.toggle('est-visible', !vus.hero && !vus.bas);
    new IntersectionObserver((e) => { vus.hero = e[0].isIntersecting; maj(); }).observe(hero);
    const bas = document.getElementById('inscription');
    if (bas) new IntersectionObserver((e) => { vus.bas = e[0].isIntersecting; maj(); }).observe(bas);
  }
}
